import React, { useContext, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import { FiSettings, FiSave, FiPlay, FiFileText, FiClock, FiAlertTriangle } from 'react-icons/fi';
import { FaBuilding, FaCalendarAlt, FaRoute } from 'react-icons/fa';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import ZForm from './../components/ZForm';

export default function Configuration() {
    const context = useContext(AppContext);
    const [monthYear, setMonthYear] = useState(1);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [company, setCompany] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    // Earlier months
    const mys = [];
    moment.locale('es');
    const m = moment().add(5, 'month');
    for (let i = 1; i < 15; i++) {
        mys.push({
            id: i,
            month: m.format('MM'),
            year: m.format('YYYY'),
            name: m.format('MMMM YYYY')
        });
        m.subtract(1, 'month');
    }
    const [monthYears] = useState(mys);

    const onDateChange = (ev) => {
        const monthYear = ev.target.value;
        const year = monthYears[monthYear - 1].year;
        const month = monthYears[monthYear - 1].month;
        setMonthYear(monthYear);
        setYear(year);
        setMonth(month);
    };

    const generateMonth = () => {
        setIsGenerating(true);
        tre.api('POST', '/trips/years/' + year + '/months/' + month, {
            context: context,
            success: () => {
                NotificationManager.success(`Generado mes ${month} y año ${year}`);
                setIsGenerating(false);
            },
            error: () => setIsGenerating(false)
        });
    };

    const generateRoutes = () => {
        tre.api('POST', '/companies/' + context.user.company_id + '/generateRoutes', {
            context: context,
            success: () => {
                NotificationManager.success('Rutas generadas');
            }
        });
    };

    const loadCompany = () => {
        setIsLoading(true);
        tre.api('GET', '/companies/' + context.idCompany, {
            context: context,
            success: (r) => {
                setCompany(r);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    const handleForm = ({ name, value }) => {
        setCompany({ ...company, [name]: value });
    };

    const saveCompany = () => {
        setIsSaving(true);
        tre.api('PUT', '/companies/' + context.idCompany, {
            context: context,
            params: company,
            success: () => {
                NotificationManager.success('Datos de la empresa modificados');
                setIsSaving(false);
            },
            error: () => setIsSaving(false)
        });
    };

    useEffect(() => {
        loadCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4">
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando configuración...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '900px' }}>
            {/* Header */}
            <div className="mb-4">
                <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                    <FiSettings className="me-2" style={{ color: '#1a73e8' }} />
                    Configuración
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    Gestiona los datos de la empresa y operaciones del sistema
                </p>
            </div>

            {/* Company Data Section */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FaBuilding size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Datos de la Empresa</h6>
                            <small className="text-muted">Información general y facturación</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <ZForm
                        elements={[
                            { label: 'Nombre', md: 8, name: 'name', value: company.name || '' },
                            { label: 'CIF', md: 4, name: 'cif', value: company.cif || '' },
                            { label: 'Dirección', md: 12, name: 'address', value: company.address || '' },
                            { label: 'Ciudad', md: 8, name: 'city', value: company.city || '' },
                            { label: 'Teléfono', md: 4, name: 'phone', value: company.phone || '' },
                            { label: 'E-mail', md: 6, name: 'email', value: company.email || '' },
                            { label: 'Nº de factura siguiente', md: 3, name: 'num_invoice', value: company.num_invoice || '' },
                        ]}
                        onChange={handleForm}
                    />
                </Card.Body>
            </Card>

            {/* Invoice Notes Section */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e6f4ea',
                                color: '#34a853'
                            }}
                        >
                            <FiFileText size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Notas de Factura</h6>
                            <small className="text-muted">Texto que aparecerá en las facturas</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <ZForm
                        elements={[
                            {
                                label: 'Notas que aparecerán en las facturas',
                                type: 'textarea',
                                md: 12,
                                name: 'invoice_notes',
                                value: company.invoice_notes || ''
                            },
                        ]}
                        onChange={handleForm}
                    />
                </Card.Body>
            </Card>

            {/* Booking Configuration Section */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#fef7e0',
                                color: '#f9ab00'
                            }}
                        >
                            <FiClock size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Configuración de Reservas</h6>
                            <small className="text-muted">Mensajes y horarios para usuarios</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <ZForm
                        elements={[
                            {
                                label: 'Mensaje para el usuario (se muestra en el calendario)',
                                type: 'textarea',
                                md: 12,
                                name: 'msg_for_user',
                                value: company.msg_for_user || ''
                            },
                            {
                                label: 'Última hora para reservar (ej: 20:00)',
                                md: 4,
                                name: 'last_time_book',
                                value: company.last_time_book || ''
                            },
                        ]}
                        onChange={handleForm}
                    />
                </Card.Body>
            </Card>

            {/* Save Button */}
            <div className="d-flex justify-content-end mb-4">
                <Button variant="primary" onClick={saveCompany} disabled={isSaving}>
                    {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                    Guardar Cambios
                </Button>
            </div>

            {/* Operations Section */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FaCalendarAlt size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Operaciones</h6>
                            <small className="text-muted">Generación de viajes y rutas</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    {/* Generate Month Trips */}
                    <div className="p-3 mb-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div className="d-flex align-items-start">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#e6f4ea',
                                    color: '#34a853'
                                }}
                            >
                                <FaCalendarAlt size={16} />
                            </div>
                            <div className="flex-grow-1">
                                <h6 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>Generar Viajes del Mes</h6>
                                <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                                    Genera todos los viajes del mes seleccionado. Solo se crean los viajes que no existan.
                                    Si anteriormente eliminaste algún viaje, se volverá a crear.
                                </p>
                                <Row className="align-items-center g-2">
                                    <Col md={6} xs={12}>
                                        <Form.Select
                                            name="monthYear"
                                            value={monthYear}
                                            onChange={(ev) => onDateChange(ev)}
                                        >
                                            {monthYears && monthYears.map(month => (
                                                <option value={month.id} key={month.id}>{month.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={4} xs={12}>
                                        <Button
                                            variant="success"
                                            onClick={generateMonth}
                                            disabled={isGenerating}
                                            className="w-100"
                                        >
                                            {isGenerating ? <Spinner size="sm" className="me-2" /> : <FiPlay className="me-2" />}
                                            Generar
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </div>

                    {/* Generate Routes - Admin Only */}
                    {context.user.id === 1 && (
                        <div className="p-3" style={{ backgroundColor: '#fef7e0', borderRadius: '8px', borderLeft: '4px solid #f9ab00' }}>
                            <div className="d-flex align-items-start">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#fff',
                                        color: '#f9ab00'
                                    }}
                                >
                                    <FaRoute size={16} />
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>Crear Rutas</h6>
                                    <p className="mb-3" style={{ fontSize: '13px', color: '#5f6368' }}>
                                        <FiAlertTriangle className="me-1" style={{ color: '#f9ab00' }} />
                                        <strong>Precaución:</strong> Crea todas las rutas.
                                        Si ya existen rutas, se crearán duplicados.
                                    </p>
                                    <Button variant="warning" onClick={generateRoutes}>
                                        <FiPlay className="me-2" />
                                        Crear Rutas
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}
